import { ChevronLeft, ChevronRight, Mail } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';

import {
  Badge,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  SelectMenu as Select,
  Spinner,
} from '@/components/ui';
import { useDebounce, usePersistedState } from '@/hooks';
import { useEmailLogs } from '@/hooks/useEmailLogs';
import { EmailLogRecord, EmailLogStatus, EmailRecipientType } from '@/services/emailLog.service';
import cn from '@/utils/cn';
import { formatDateTime } from '@/utils/format';

const STATUS_OPTIONS: EmailLogStatus[] = [
  'pending',
  'queued',
  'sent',
  'delivered',
  'bounced',
  'failed',
  'complained',
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const STATUS_TONE: Record<EmailLogStatus, string> = {
  pending: 'bg-secondary text-muted-foreground',
  queued: 'bg-secondary text-muted-foreground',
  sent: 'bg-success/15 text-success',
  delivered: 'bg-success/15 text-success',
  bounced: 'bg-danger/15 text-danger',
  failed: 'bg-danger/15 text-danger',
  complained: 'bg-warning/20 text-foreground',
};

const TH =
  'whitespace-nowrap px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-ink/40';
const TD = 'px-4 py-3 text-sm align-top';

interface EmailLogsFilters {
  search: string;
  status: '' | EmailLogStatus;
  recipientType: '' | EmailRecipientType;
  from: string;
  to: string;
  page: number;
  limit: number;
}

const DEFAULT_FILTERS: EmailLogsFilters = {
  search: '',
  status: '',
  recipientType: '',
  from: '',
  to: '',
  page: 1,
  limit: 20,
};

const StatusPill = ({ status }: { status: EmailLogStatus }) => (
  <span
    className={cn(
      'inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold',
      STATUS_TONE[status],
    )}
  >
    {status}
  </span>
);

const DetailRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid grid-cols-1 gap-1 border-b border-border/60 py-2 last:border-b-0 sm:grid-cols-[120px_1fr] sm:gap-3">
    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </span>
    <span className="min-w-0 break-words text-sm text-foreground">{children || '-'}</span>
  </div>
);

interface DetailDialogProps {
  log: EmailLogRecord | null;
  onClose: () => void;
}

const EmailLogDetailDialog = ({ log, onClose }: DetailDialogProps) => (
  <Dialog
    open={log !== null}
    onOpenChange={(open) => {
      if (!open) onClose();
    }}
  >
    <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Email details</DialogTitle>
        <DialogDescription>Read-only audit record. The email body is not stored.</DialogDescription>
      </DialogHeader>
      {log && (
        <div className="flex flex-col">
          <DetailRow label="Status">
            <StatusPill status={log.status} />
          </DetailRow>
          <DetailRow label="Date & Time">{formatDateTime(log.sentAt ?? log.createdAt)}</DetailRow>
          <DetailRow label="Reference No.">
            <span className="font-mono text-xs">{log.referenceNumber ?? '-'}</span>
          </DetailRow>
          <DetailRow label="From">
            {log.senderName ?? `#${log.senderId}`}
            {(log.fromEmail ?? log.senderEmail) ? ` <${log.fromEmail ?? log.senderEmail}>` : ''}
          </DetailRow>
          <DetailRow label="Employee Code">{log.senderEmployeeCode ?? '-'}</DetailRow>
          <DetailRow label="Code">{log.recipientType ?? '-'}</DetailRow>
          <DetailRow label="To">
            <div className="flex flex-col gap-0.5">
              {log.recipientName && <span className="font-medium">{log.recipientName}</span>}
              <span>{log.toAddresses.join(', ') || '-'}</span>
            </div>
          </DetailRow>
          <DetailRow label="CC">{log.ccAddresses?.join(', ')}</DetailRow>
          <DetailRow label="BCC">{log.bccAddresses?.join(', ')}</DetailRow>
          <DetailRow label="Reply-To">{log.replyTo}</DetailRow>
          <DetailRow label="Template">{log.templateName}</DetailRow>
          <DetailRow label="Subject">{log.subject}</DetailRow>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

const Pagination = ({
  page,
  totalPages,
  total,
  limit,
  onPage,
  onLimit,
}: {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPage: (p: number) => void;
  onLimit: (l: number) => void;
}) => (
  <div className="flex flex-wrap items-center justify-between gap-3">
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <span>Rows per page</span>
      <Select
        value={String(limit)}
        onChange={(e) => onLimit(Number(e.target.value))}
        className="w-20"
      >
        {PAGE_SIZE_OPTIONS.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </Select>
      <span>{`${total} email(s)`}</span>
    </div>
    <div className="flex items-center gap-2">
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm disabled:opacity-50"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
      >
        <ChevronLeft className="h-4 w-4" />
        Prev
      </button>
      <span className="text-sm text-muted-foreground">{`${page} / ${totalPages}`}</span>
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm disabled:opacity-50"
        disabled={page >= totalPages}
        onClick={() => onPage(page + 1)}
      >
        Next
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  </div>
);

interface EmailLogsPageProps {
  storageKey?: string;
}

const EmailLogsPage = ({ storageKey = 'email-logs-filters' }: EmailLogsPageProps) => {
  const [filters, setFilters] = usePersistedState<EmailLogsFilters>(storageKey, DEFAULT_FILTERS);
  const debouncedSearch = useDebounce(filters.search, 300);
  const [selected, setSelected] = useState<EmailLogRecord | null>(null);

  // Any filter change (other than the page itself) returns to page 1.
  const patch = (next: Partial<EmailLogsFilters>) =>
    setFilters((prev) => ({
      ...prev,
      ...next,
      ...('page' in next ? {} : { page: 1 }),
    }));

  // A persisted filter object saved before `limit` existed (usePersistedState
  // stores the raw shape, it does not merge new defaults) would leave limit
  // undefined; fall back so the page size and the dropdown stay valid.
  const limit = filters.limit ?? DEFAULT_FILTERS.limit;

  const { data, isLoading, isError } = useEmailLogs({
    page: filters.page,
    limit,
    search: debouncedSearch || undefined,
    status: filters.status || undefined,
    recipientType: filters.recipientType || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
  });

  const rows = data?.rows ?? [];
  const totalPages = data?.totalPages ?? 1;

  // Clamp page only once data has loaded, so a persisted page (e.g. page 3 on
  // refresh) isn't reset to 1 against the default totalPages of 1 mid-load.
  useEffect(() => {
    if (data && filters.page > totalPages) setFilters((prev) => ({ ...prev, page: totalPages }));
  }, [data, totalPages, filters.page, setFilters]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Mail className="h-5 w-5 text-brand" />
        <h1 className="text-lg font-bold">Email Logs</h1>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search reference, subject or employee (name / code)"
          value={filters.search}
          onChange={(e) => patch({ search: e.target.value })}
          className="min-w-[220px] flex-1"
        />
        <Select
          value={filters.status}
          onChange={(e) => patch({ status: e.target.value as EmailLogsFilters['status'] })}
          className="w-40"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
        <Select
          value={filters.recipientType}
          onChange={(e) =>
            patch({ recipientType: e.target.value as EmailLogsFilters['recipientType'] })
          }
          className="w-40"
        >
          <option value="">All recipients</option>
          <option value="C">Client / Lead</option>
          <option value="E">Employee / Team</option>
        </Select>
        <DatePicker
          value={filters.from}
          onChange={(v) => patch({ from: v })}
          placeholder="From"
          max={filters.to || undefined}
        />
        <DatePicker
          value={filters.to}
          onChange={(v) => patch({ to: v })}
          placeholder="To"
          min={filters.from || undefined}
        />
      </div>

      <Pagination
        page={filters.page}
        totalPages={totalPages}
        total={data?.total ?? 0}
        limit={limit}
        onPage={(p) => patch({ page: p })}
        onLimit={(l) => patch({ limit: l })}
      />

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {/* The height bound must stay unconditional: it is what makes this div a
            scroll container, and without a scrolling ancestor the sticky <thead>
            below has nothing to stick within and scrolls away. The md: variant only
            tightens the desktop value (page chrome above the table is ~320px there);
            phones keep the plain 70dvh cap. dvh, not vh, so the iOS URL bar
            collapsing does not leave the box taller than the visible viewport. */}
        <div className="max-h-[70dvh] overflow-auto md:max-h-[min(70dvh,calc(100dvh-320px))]">
          <table className="w-full min-w-[1040px] border-collapse">
            <thead className="sticky top-0 z-10 border-b border-border bg-secondary/95 backdrop-blur">
              <tr>
                <th className={TH}>From (Employee)</th>
                <th className={TH}>Emp. Code</th>
                <th className={TH}>To</th>
                <th className={TH}>Code</th>
                <th className={TH}>Reference No.</th>
                <th className={TH}>Subject</th>
                <th className={TH}>Status</th>
                <th className={TH}>Date &amp; Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading && (
                <tr>
                  <td className={TD} colSpan={8}>
                    <div className="flex justify-center py-6">
                      <Spinner />
                    </div>
                  </td>
                </tr>
              )}
              {isError && !isLoading && (
                <tr>
                  <td className={`${TD} text-danger`} colSpan={8}>
                    Could not load email logs.
                  </td>
                </tr>
              )}
              {!isLoading && !isError && rows.length === 0 && (
                <tr>
                  <td className={`${TD} text-muted-foreground`} colSpan={8}>
                    No emails match these filters.
                  </td>
                </tr>
              )}
              {/* Row activation is a real <button> in the first cell rather
                  than `role="button"` on the <tr>: that role is
                  children-presentational and would collapse the whole log row
                  (recipient, reference, status, timestamp) into a single
                  unreadable label in screen-reader table mode. */}
              {rows.map((log) => (
                <tr
                  key={log.id}
                  className="cursor-pointer hover:bg-secondary/30"
                  onClick={() => setSelected(log)}
                >
                  <td className={TD}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected(log);
                      }}
                      className="rounded text-left font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="sr-only">{`Open details for ${log.subject}: `}</span>
                      {log.senderName ?? `#${log.senderId}`}
                    </button>
                    {(log.fromEmail ?? log.senderEmail) && (
                      <div
                        className="max-w-[200px] truncate text-[11px] text-muted-foreground"
                        title={log.fromEmail ?? log.senderEmail ?? undefined}
                      >
                        {log.fromEmail ?? log.senderEmail}
                      </div>
                    )}
                  </td>
                  <td className={`${TD} whitespace-nowrap font-mono text-xs`}>
                    {log.senderEmployeeCode ?? '-'}
                  </td>
                  <td className={TD}>
                    <div className="font-medium">
                      {log.recipientName ?? log.toAddresses[0] ?? '-'}
                    </div>
                    <div
                      className="max-w-[220px] truncate text-[11px] text-muted-foreground"
                      title={log.toAddresses.join(', ')}
                    >
                      {log.toAddresses.join(', ')}
                    </div>
                  </td>
                  <td className={TD}>
                    {log.recipientType && (
                      <Badge variant={log.recipientType === 'C' ? 'info' : 'neutral'}>
                        {log.recipientType}
                      </Badge>
                    )}
                  </td>
                  <td className={`${TD} whitespace-nowrap font-mono text-xs`}>
                    {log.referenceNumber ?? '-'}
                  </td>
                  <td className={TD}>
                    <div className="max-w-[260px] truncate" title={log.subject}>
                      {log.subject}
                    </div>
                  </td>
                  <td className={TD}>
                    <StatusPill status={log.status} />
                  </td>
                  <td className={`${TD} whitespace-nowrap text-muted-foreground`}>
                    {formatDateTime(log.sentAt ?? log.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <EmailLogDetailDialog log={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default EmailLogsPage;
